<script setup>
import { ref, watch } from "vue";
import SimpleTable from "@/components/table/SimpleTable.vue";
import ArgonTd from "@/components/table/ArgonTd.vue";
import EditableRowActions from "@/components/table/EditableRowActions.vue";
import EditableGridAddRow from "@/components/table/EditableGridAddRow.vue";
import CopyValue from "@/components/CopyValue.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import { useEditableRows } from "@/composables/useEditableRows";
import { HANDLING_TYPE_LABELS, formatHandlingType } from "@/utils/formatters";

const HANDLING_TYPE_OPTIONS = Object.entries(HANDLING_TYPE_LABELS).map(
  ([value, label]) => ({ value, label })
);

const props = defineProps({
  packages: { type: Array, default: () => [] },
  packagesTouched: { type: Boolean, default: false },
});

const emit = defineEmits(["remove-package", "add-package", "prev", "next"]);

const editState = useEditableRows();
const saveErrors = ref({
  barcode: false,
  weight: false,
  content_description: false,
  origin_address: false,
});

function resetSaveErrors() {
  saveErrors.value = {
    barcode: false,
    weight: false,
    content_description: false,
    origin_address: false,
  };
}

function createEmptyPackage() {
  return {
    task_id: null,
    barcode: "",
    weight: "",
    volumetric_weight: "",
    handling_type: "ESTANDAR",
    content_description: "",
    origin_address: "",
  };
}

function toNumber(value) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function validateRow(item) {
  const errors = {
    barcode: !String(item.barcode || "").trim(),
    weight: !(toNumber(item.weight) > 0),
    content_description: !String(item.content_description || "").trim(),
    origin_address: !String(item.origin_address || "").trim(),
  };
  saveErrors.value = errors;
  return !Object.values(errors).some(Boolean);
}

const newRowFirstInputRef = ref(null);
const addRowRef = ref(null);
const packagesTableRef = ref(null);

function addNewRow() {
  if (editState.isEditingAny()) return;
  const row = editState.createNewRow(createEmptyPackage);
  emit("add-package", row);
  resetSaveErrors();
  editState.startEdit(row, { focusRef: newRowFirstInputRef, scrollRef: packagesTableRef });
}

function editRow(item) {
  if (editState.isEditingAny()) return;
  resetSaveErrors();
  editState.startEdit(item, { focusRef: newRowFirstInputRef });
}

function saveRow(item) {
  editState.confirmEdit(item, { validate: validateRow, focusRef: addRowRef });
}

function cancelRow(item) {
  editState.cancelEdit(item, {
    onDiscardNew: () => emit("remove-package", item),
  });
}

function deleteRow(item) {
  emit("remove-package", item);
}

function onPackageRowEnter(item) {
  if (item) {
    if (editState.isEditing(item)) saveRow(item);
    return;
  }
  if (!editState.isEditingAny()) addNewRow();
}

function isPackageFieldEmpty(item, field) {
  if (field === "weight") return !(toNumber(item.weight) > 0);
  return !String(item[field] || "").trim();
}

function findFirstInvalidPackage() {
  return props.packages.find(
    (item) =>
      isPackageFieldEmpty(item, "barcode") ||
      isPackageFieldEmpty(item, "content_description") ||
      isPackageFieldEmpty(item, "origin_address") ||
      isPackageFieldEmpty(item, "weight")
  );
}

watch(
  () => props.packagesTouched,
  (touched) => {
    if (!touched || editState.isEditingAny()) return;
    const invalid = findFirstInvalidPackage();
    if (invalid) editRow(invalid);
  }
);

const bultosHeaders = [
  { header: "Código barras", field: "barcode", width: "deca-col-barcode" },
  { header: "Manipulación", field: "handling_type", position: "start", width: "deca-col-handling" },
  { header: "Naturaleza", field: "content_description", position: "start", width: "deca-col-description" },
  { header: "Origen", field: "origin_address", position: "start", width: "deca-col-origin" },
  { header: "Kg", field: "weight", position: "start", width: "deca-col-weight" },
  { header: "m³", field: "volumetric_weight", position: "start", width: "deca-col-volumetric" },
  { header: "", field: "actions", position: "center", width: "deca-col-actions" },
];

function getBultosRows() {
  return { total: props.packages.length, data: props.packages };
}

function getBultosPosition(field) {
  return bultosHeaders.find((column) => column.field === field)?.position || "start";
}

defineExpose({
  isEditingAny: () => editState.isEditingAny(),
});
</script>

<template>
  <div
    class="multisteps-form__panel js-active p-2 position-relative bg-white rounded-3"
    data-animation="FadeIn"
  >
    <div class="d-flex flex-column" style="height: 420px; overflow-y: auto; overflow-x: hidden;">
      <div class="text-center mb-3">
        <p class="mb-0">Revisa los datos de los bultos</p>
      </div>

      <div class="mb-3">
        <simple-table
          ref="packagesTableRef"
          :headers="bultosHeaders"
          :items="getBultosRows()"
          empty-message="Sin bultos"
          :show-line-separator="true"
          :max-height="330"
          :min-height="330"
          :body-min-height="260"
          fixed-layout
          @row-enter="onPackageRowEnter"
        >
          <template #default="{ item }">
            <argon-td
              v-if="!editState.isEditing(item)"
              :text-primary="item.barcode ?? ''"
              :position="getBultosPosition('barcode')"
              max-width="150px"
            >
              <template v-if="item.barcode" #append>
                <copy-value :value-to-copy="item.barcode" size="xs" show-on-hover />
              </template>
            </argon-td>
            <argon-td
              v-else
              :position="getBultosPosition('barcode')"
              :text-primary="item.isNew ? '' : (item.barcode ?? '')"
              max-width="150px"
            >
              <input
                v-if="item.isNew"
                ref="newRowFirstInputRef"
                v-model="item.barcode"
                type="text"
                class="form-control form-control-sm editable-cell-input"
                :class="{ 'is-invalid': saveErrors.barcode }"
              />
            </argon-td>

            <argon-td
              v-if="!editState.isEditing(item)"
              :text-primary="formatHandlingType(item.handling_type)"
              :position="getBultosPosition('handling_type')"
            />
            <argon-td v-else :position="getBultosPosition('handling_type')">
              <select
                v-model="item.handling_type"
                class="form-select form-select-sm editable-cell-input"
              >
                <option
                  v-for="option in HANDLING_TYPE_OPTIONS"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </argon-td>

            <argon-td
              v-if="!editState.isEditing(item)"
              :text-primary="item.content_description || '-'"
              :position="getBultosPosition('content_description')"
              max-width="100%"
            />
            <argon-td v-else :position="getBultosPosition('content_description')">
              <input
                v-model="item.content_description"
                type="text"
                maxlength="80"
                class="form-control form-control-sm editable-cell-input"
                :class="{ 'is-invalid': saveErrors.content_description }"
              />
            </argon-td>

            <argon-td
              v-if="!editState.isEditing(item)"
              :text-primary="item.origin_address || '-'"
              :position="getBultosPosition('origin_address')"
              max-width="100%"
            />
            <argon-td v-else :position="getBultosPosition('origin_address')">
              <input
                v-model="item.origin_address"
                type="text"
                maxlength="180"
                class="form-control form-control-sm editable-cell-input"
                :class="{ 'is-invalid': saveErrors.origin_address }"
              />
            </argon-td>

            <argon-td
              v-if="!editState.isEditing(item)"
              :text-primary="item.weight ? String(item.weight) : '-'"
              :position="getBultosPosition('weight')"
            />
            <argon-td v-else :position="getBultosPosition('weight')">
              <input
                v-model.number="item.weight"
                type="number"
                min="0"
                step="0.01"
                class="form-control form-control-sm editable-cell-input"
                :class="{ 'is-invalid': saveErrors.weight }"
              />
            </argon-td>

            <argon-td
              v-if="!editState.isEditing(item)"
              :text-primary="item.volumetric_weight ? String(item.volumetric_weight) : '-'"
              :position="getBultosPosition('volumetric_weight')"
            />
            <argon-td v-else :position="getBultosPosition('volumetric_weight')">
              <input
                v-model.number="item.volumetric_weight"
                type="number"
                min="0"
                step="0.001"
                class="form-control form-control-sm editable-cell-input"
              />
            </argon-td>

            <argon-td :position="getBultosPosition('actions')">
              <editable-row-actions
                :editing="editState.isEditing(item)"
                :disabled="editState.isEditingAny()"
                @edit="editRow(item)"
                @save="saveRow(item)"
                @cancel="cancelRow(item)"
                @delete="deleteRow(item)"
              />
            </argon-td>
          </template>
          <template #foot-table>
            <editable-grid-add-row
              ref="addRowRef"
              :colspan="bultosHeaders.length"
              label="Nuevo bulto"
              :disabled="editState.isEditingAny()"
              @click="addNewRow"
            />
          </template>
        </simple-table>
      </div>

      <div class="d-flex mt-auto">
        <argon-button
          class="mb-0 btn-icon-only"
          color="primary"
          variant="outline"
          size="xs"
          aria-label="Anterior"
          @click.prevent="emit('prev')"
        >
          <span class="btn-inner--icon">
            <i class="fas fa-chevron-left" aria-hidden="true"></i>
          </span>
        </argon-button>
        <argon-button
          class="mb-0 ms-auto btn-icon-only"
          color="primary"
          variant="outline"
          size="xs"
          aria-label="Siguiente"
          @click.prevent="emit('next')"
        >
          <span class="btn-inner--icon">
            <i class="fas fa-chevron-right" aria-hidden="true"></i>
          </span>
        </argon-button>
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(.deca-col-barcode) {
  width: 13%;
}

:deep(.deca-col-handling) {
  width: 13%;
}

:deep(.deca-col-description) {
  width: 23%;
}

:deep(.deca-col-origin) {
  width: 25%;
}

:deep(.deca-col-weight) {
  width: 9%;
}

:deep(.deca-col-volumetric) {
  width: 9%;
}

:deep(.deca-col-actions) {
  width: 8%;
}
</style>
